import { useState } from 'react';
import { Building2, ChevronRight } from 'lucide-react';
import { usePerLocationRevenue } from '@/hooks/usePerLocationRevenue';
import type { PerLocationRevenueRange } from '@evi/shared';
import { formatCurrency } from './_lib';
import styles from '../DashboardPage.module.css';

/**
 * Per-location revenue widget. All locations in the caller's
 * scope with their windowed revenue, sorted desc. Differs from
 * the Top Revenue Locations widget (which shows only top 10
 * for a leaderboard-style operator view); this widget is the
 * comprehensive per-property report for Building Admins /
 * Property Managers managing multiple buildings.
 *
 * Reuses the topRev row styling — same data shape per row
 * (rank-numbered list with name, session count, revenue, click-
 * through chevron). The semantic difference is the bound: top
 * widget caps at 10, this widget shows everything in scope. A
 * super_admin sees every location across the platform; the list
 * scrolls within the widget body via max-height + overflow.
 */
export default function PerLocationRevenueWidget({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const [range, setRange] = useState<PerLocationRevenueRange>('7d');
  const q = usePerLocationRevenue(range);
  const data = q.data;

  return (
    <div className={styles.chartBody}>
      <div className={styles.topRevHeader}>
        <div className={styles.chartIcon}><Building2 size={14} /></div>
        <p className={styles.chartTitle}>Revenue by location</p>
        <div className={styles.topRevToggle} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={range === '7d'}
            className={`${styles.topRevToggleBtn} ${range === '7d' ? styles.topRevToggleBtnActive : ''}`}
            onClick={() => setRange('7d')}
          >
            7d
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={range === '30d'}
            className={`${styles.topRevToggleBtn} ${range === '30d' ? styles.topRevToggleBtnActive : ''}`}
            onClick={() => setRange('30d')}
          >
            30d
          </button>
        </div>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><Building2 size={18} /></span>
          <p>
            {data.is_scoped
              ? `No revenue at your assigned locations in the last ${range === '30d' ? '30 days' : '7 days'}.`
              : `No revenue in the last ${range === '30d' ? '30 days' : '7 days'}.`}
          </p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className={styles.topRevList}>
          {data.rows.map((row, idx) => (
            <a
              key={row.location_id}
              href={`/locations/${row.location_id}`}
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate(`/locations/${row.location_id}`);
                }
              }}
              className={styles.topRevRow}
            >
              <span className={styles.topRevRank}>{idx + 1}</span>
              <div className={styles.topRevBody}>
                <span className={styles.topRevName}>{row.location_name}</span>
                <span className={styles.topRevSessionCount}>
                  {row.session_count} session{row.session_count === 1 ? '' : 's'}
                </span>
              </div>
              <span className={styles.topRevAmount}>
                {formatCurrency(row.revenue_cents / 100)}
              </span>
              <ChevronRight size={14} className={styles.topRevChevron} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
